import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  Plus,
  Calendar,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Phone,
  Mail,
  ShoppingCart,
  User,
  Clock,
  DollarSign,
  Package,
  Send,
  ArrowDownCircle,
  ChevronDown,
  X,
  CheckCircle,
  PhoneCall,
  Train
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Order, OrderQueryRequest, Technician, DarkStore, ServiceType } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';

// ─── Async product search (live API, no pre-load limit) ────────────────────
const AsyncProductSelect: React.FC<{
  value: number | null;
  onChange: (id: number | null, item: ServiceType | null) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<ServiceType | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await apiService.getProducts(1, 30, q);
        setResults(res?.entity?.items ?? []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
  }, [q, open]);

  const handleSelect = (item: ServiceType) => {
    setSelected(item);
    onChange(item.id, item);
    setOpen(false);
    setQ('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected(null);
    onChange(null, null);
  };

  const label = selected ? `${selected.makeName} ${selected.modelName} — ${selected.serviceName}` : null;

  return (
    <div ref={ref} className="relative">
      <div onClick={() => setOpen(!open)}
        className="flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-lg cursor-pointer bg-white hover:border-blue-400">
        <span className={label ? 'text-gray-900 truncate pr-2' : 'text-gray-400'}>{label ?? 'Search product…'}</span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {selected && <button onMouseDown={handleClear} className="text-gray-400 hover:text-gray-600"><X className="h-3 w-3" /></button>}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </div>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
              placeholder="Type brand, model or service…"
              className="w-full text-sm px-2 py-1 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500" />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {loading
              ? <div className="px-3 py-3 text-sm text-gray-400 text-center">Searching…</div>
              : results.length === 0
                ? <div className="px-3 py-3 text-sm text-gray-400 text-center">{q ? 'No results' : 'Start typing to search'}</div>
                : results.map(item => (
                  <div key={item.id} onMouseDown={() => handleSelect(item)}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 ${item.id === value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'}`}>
                    <span className="font-medium">{item.makeName} {item.modelName}</span>
                    <span className="text-gray-500"> — {item.serviceName}</span>
                  </div>
                ))
            }
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Issue Parts Modal (pre-filled from order) ─────────────────────────────
const IssuePartsModal: React.FC<{
  order: Order;
  darkStores: DarkStore[];
  onClose: () => void;
  onSuccess: () => void;
}> = ({ order, darkStores, onClose, onSuccess }) => {
  const [serviceId, setServiceId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [darkStoreId, setDarkStoreId] = useState<number | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [availableQty, setAvailableQty] = useState<number | null>(null);
  const [loadingQty, setLoadingQty] = useState(false);

  // Quick-pick from order's own services
  const orderServices = order.orderItems ?? [];

  useEffect(() => {
    if (!serviceId || !darkStoreId) { setAvailableQty(null); return; }
    setLoadingQty(true);
    apiService.getInventory({ darkStoreId, serviceId, pageSize: 1 })
      .then((data: any) => {
        const items = data?.items ?? data ?? [];
        const found = Array.isArray(items) ? items.find((i: any) => i.serviceId === serviceId) : null;
        setAvailableQty(found ? found.quantity : 0);
      })
      .catch(() => setAvailableQty(null))
      .finally(() => setLoadingQty(false));
  }, [serviceId, darkStoreId]);

  const stockBadge = () => {
    if (!darkStoreId || !serviceId) return null;
    if (loadingQty) return <span className="text-xs text-gray-400 animate-pulse">Checking…</span>;
    if (availableQty === null) return null;
    if (availableQty === 0) return <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Out of stock</span>;
    if (availableQty <= 5) return <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Low · {availableQty} left</span>;
    return <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{availableQty} in stock</span>;
  };

  const handleSubmit = async () => {
    if (!serviceId || quantity <= 0) { toast.error('Select a product and quantity'); return; }
    if (!order.technicianId) { toast.error('No technician assigned to this order'); return; }
    if (darkStoreId && availableQty !== null && quantity > availableQty) {
      toast.error(`Only ${availableQty} unit(s) available`); return;
    }
    setSaving(true);
    try {
      const result = await apiService.issueTechnicianStock({
        technicianId: order.technicianId,
        serviceId,
        quantity,
        orderId: order.id,
        darkStoreId: darkStoreId ?? null,
        notes: notes || undefined,
      });
      if (result.whatsappWarning) {
        toast.success(`Parts issued to ${order.technicianName}`);
        toast.error(`WhatsApp: ${result.whatsappWarning}`, { duration: 6000 });
      } else {
        toast.success(`Parts issued to ${order.technicianName} — WhatsApp sent`);
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to issue parts');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <ArrowDownCircle className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Issue Parts to Technician</h2>
              <p className="text-xs text-gray-500">Order {order.orderNumber} · {order.technicianName || 'No technician assigned'}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Order context banner */}
          <div className="flex gap-4 p-3 bg-gray-50 rounded-lg text-sm">
            <div><span className="text-gray-500">Order</span> <span className="font-medium text-gray-900">{order.orderNumber}</span></div>
            <div><span className="text-gray-500">Customer</span> <span className="font-medium text-gray-900">{order.customerName}</span></div>
            <div><span className="text-gray-500">Device</span> <span className="font-medium text-gray-900">{order.makeName} {order.modelName}</span></div>
            <div><span className="text-gray-500">Technician</span> <span className={`font-medium ${order.technicianName ? 'text-gray-900' : 'text-red-500'}`}>{order.technicianName || 'Not assigned'}</span></div>
          </div>

          {!order.technicianId && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              Assign a technician to this order before issuing parts.
            </div>
          )}

          {/* Quick pick from order items */}
          {orderServices.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Quick Pick from Order Services</label>
              <div className="flex flex-wrap gap-2">
                {orderServices.map(item => (
                  <button key={item.serviceId}
                    onClick={() => setServiceId(item.serviceId)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                      serviceId === item.serviceId
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400'
                    }`}>
                    {item.serviceName}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Row 1: Dark Store + Product + Qty */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Dark Store (From)</label>
              <select value={darkStoreId ?? ''} onChange={e => setDarkStoreId(e.target.value ? Number(e.target.value) : null)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">— None —</option>
                {darkStores.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-gray-600">Product / Service *</label>
                {stockBadge()}
              </div>
              <AsyncProductSelect value={serviceId} onChange={(id) => setServiceId(id)} />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Quantity *</label>
              <input type="number" min={1}
                max={availableQty !== null && darkStoreId ? availableQty : undefined}
                value={quantity} onChange={e => setQuantity(Number(e.target.value))}
                className={`w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  darkStoreId && availableQty !== null && quantity > availableQty ? 'border-red-400 bg-red-50' : 'border-gray-200'
                }`} />
              {darkStoreId && availableQty !== null && quantity > availableQty && (
                <p className="text-xs text-red-600 mt-1">Exceeds stock ({availableQty})</p>
              )}
            </div>
          </div>

          {/* Row 2: Notes + WhatsApp hint */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Notes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
            </div>
            <div className="flex items-end">
              <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg w-full">
                <CheckCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700">WhatsApp sent to {order.technicianName || 'technician'} on issue.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-5 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
          <button onClick={handleSubmit}
            disabled={saving || !order.technicianId || (darkStoreId !== null && availableQty === 0)}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50">
            {saving ? 'Issuing…' : 'Issue & Notify Technician'}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Parse "2:30 PM" or "14:30:00" from DB into a local Date. */
function parseScheduledDt(scheduledDate: string, scheduledTime: string): Date | null {
  try {
    const datePart = scheduledDate.split('T')[0];
    const [y, mo, d] = datePart.split('-').map(Number);
    const match = scheduledTime.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
    if (!match) return null;
    let h = parseInt(match[1], 10);
    const m = parseInt(match[2], 10);
    const period = match[3]?.toUpperCase();
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return new Date(y, mo - 1, d, h, m, 0, 0);
  } catch { return null; }
}

function scheduleChip(scheduledDate: string, scheduledTime: string): React.ReactNode {
  const dt = parseScheduledDt(scheduledDate, scheduledTime);
  if (!dt) return null;
  const diff = dt.getTime() - Date.now();
  const delayed = diff < 0;
  const absMins = Math.round(Math.abs(diff) / 60000);
  const h = Math.floor(absMins / 60);
  const m = absMins % 60;
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  const displayTime = scheduledTime.replace(/:00(\s*(AM|PM))/i, '$1');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: '#ede9fe', color: '#6d28d9', borderRadius: 20, padding: '2px 7px', fontSize: 11, fontWeight: 600, width: 'fit-content' }}>
        <Clock className="h-2.5 w-2.5" />
        {displayTime}
      </span>
      <span style={{ display: 'inline-flex', alignItems: 'center', background: delayed ? '#fee2e2' : '#d1fae5', color: delayed ? '#b91c1c' : '#065f46', borderRadius: 20, padding: '2px 7px', fontSize: 11, fontWeight: 700, width: 'fit-content' }}>
        {delayed ? `Delayed ${parts.join(' ')}` : `${parts.join(' ')} left`}
      </span>
    </div>
  );
}

const OrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [technicianFilter, setTechnicianFilter] = useState('');
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [selectedDateFilter, setSelectedDateFilter] = useState<'today' | 'tomorrow' | 'next7days' | 'custom'>('today');

  const getTodayString = () => new Date().toISOString().split('T')[0];

  const getDateFilterRange = (filter: 'today' | 'tomorrow' | 'next7days') => {
    const today = new Date();
    const todayStr = getTodayString();
    let start: string;
    let end: string;
    switch (filter) {
      case 'today':
        start = todayStr;
        end = todayStr;
        break;
      case 'tomorrow': {
        const d = new Date(today);
        d.setDate(today.getDate() + 1);
        start = d.toISOString().split('T')[0];
        end = start;
        break;
      }
      case 'next7days': {
        const d = new Date(today);
        d.setDate(today.getDate() + 7);
        start = todayStr;
        end = d.toISOString().split('T')[0];
        break;
      }
    }
    return { start, end };
  };

  const { start: defaultStart, end: defaultEnd } = getDateFilterRange('today');
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
const [showActions, setShowActions] = useState<number | null>(null);
  // Close action menu when clicking anywhere outside
  useEffect(() => {
    if (showActions === null) return;
    const handler = () => setShowActions(null);
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showActions]);

  const [issuePartsOrder, setIssuePartsOrder] = useState<Order | null>(null);
  const [darkStores, setDarkStores] = useState<DarkStore[]>([]);
  const [daySummary, setDaySummary] = useState<{ totalExpected: number; closed: number; waiting: number; totalOrders: number; closedOrders: number; waitingOrders: number; totalDiscount: number; totalServiceCost: number } | null>(null);

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'Created', label: 'Created' },
    { value: 'Assigned', label: 'Assigned' },
    { value: 'Service InProgress', label: 'Service InProgress' },
    { value: 'Service Done', label: 'Service Done' },
    { value: 'Payment Received', label: 'Payment Received' },
    { value: 'Cancelled', label: 'Cancelled' }
  ];

  useEffect(() => {
    apiService.getTechnicians().then(r => { if (r.isSuccess && r.entity) setTechnicians(r.entity); });
    apiService.getDarkStores().then((d: any) => setDarkStores(Array.isArray(d) ? d : (d?.entity ?? [])));
  }, []);

  useEffect(() => {
    loadOrders();
    loadDaySummary();
  }, [currentPage, search, statusFilter, technicianFilter, startDate, endDate]);

  const loadDaySummary = async () => {
    try {
      const response = await apiService.searchOrders(
        1, 500, '',
        startDate ? new Date(startDate) : undefined,
        endDate ? new Date(endDate) : undefined,
        statusFilter || undefined,
        technicianFilter ? Number(technicianFilter) : undefined
      );
      if (response.isSuccess && response.entity?.items) {
        const all = response.entity.items.filter((o: Order) => o.orderStatus !== 'Cancelled');
        const closed = all.filter((o: Order) => o.orderStatus === 'Payment Received');
        const waiting = all.filter((o: Order) => o.orderStatus !== 'Payment Received');
        setDaySummary({
          totalOrders: all.length,
          closedOrders: closed.length,
          waitingOrders: waiting.length,
          totalExpected: all.reduce((s: number, o: Order) => s + (o.finalAmount || 0), 0),
          closed: closed.reduce((s: number, o: Order) => s + (o.finalAmount || 0), 0),
          waiting: waiting.reduce((s: number, o: Order) => s + (o.finalAmount || 0), 0),
          totalDiscount: all.reduce((s: number, o: Order) => s + (o.discountAmount || 0), 0),
          totalServiceCost: all.reduce((s: number, o: Order) => s + (o.totalServiceCost || 0), 0),
        });
      }
    } catch {
      // silently ignore
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      console.log('Loading orders with params:', { 
        page: currentPage, 
        pageSize, 
        searchTerm: search, 
        status: statusFilter,
        startDate,
        endDate 
      });

      const response = await apiService.searchOrders(
        currentPage,
        pageSize,
        search || '',
        search ? undefined : (startDate ? new Date(startDate) : undefined),
        search ? undefined : (endDate ? new Date(endDate) : undefined),
        statusFilter || undefined,
        technicianFilter ? Number(technicianFilter) : undefined
      );

      console.log('API Response:', response);

      if (response.isSuccess && response.entity) {
        console.log('Response entity:', response.entity);
        console.log('Items:', response.entity.items);
        
        const orders = response.entity.items || [];
        const totalCount = response.entity.totalCount || 0;
        
        setOrders(orders);
        setTotalCount(totalCount);
        
        console.log('Set orders:', orders.length, 'Total count:', totalCount);
      } else {
        console.error('API response not successful:', response);
        toast.error(response.errors?.join(', ') || 'Failed to load orders');
        setOrders([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Error connecting to server. Please check if the backend is running.');
      setOrders([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const applyDateFilter = (filter: 'today' | 'tomorrow' | 'next7days') => {
    const { start, end } = getDateFilterRange(filter);
    setSelectedDateFilter(filter);
    setStartDate(start);
    setEndDate(end);
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setSearch('');
    setStatusFilter('');
    setTechnicianFilter('');
    setCurrentPage(1);
    applyDateFilter('today');
  };

  const getStatusBadge = (status: string) => {
    const statusColors: { [key: string]: string } = {
      'Created': 'bg-blue-100 text-blue-800',
      'Assigned': 'bg-yellow-100 text-yellow-800',
      'Service InProgress': 'bg-orange-100 text-orange-800',
      'Service Done': 'bg-green-100 text-green-800',
      'Payment With Technician': 'bg-amber-100 text-amber-800',
      'Payment Received': 'bg-emerald-100 text-emerald-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (amount: number) => {
    return `₹${Math.round(amount).toLocaleString('en-IN')}`;
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-IN');
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handleDeleteOrder = async (orderId: number, orderNumber: string) => {
    if (confirm(`Are you sure you want to delete order ${orderNumber}? This action cannot be undone.`)) {
      try {
        const response = await apiService.deleteOrder(orderId);
        if (response.isSuccess) {
          toast.success('Order deleted successfully');
          loadOrders(); // Reload the orders list
        } else {
          toast.error(response.errors?.join(', ') || 'Failed to delete order');
        }
      } catch (error) {
        console.error('Error deleting order:', error);
        toast.error('Error deleting order');
      }
    }
  };

  const handleSendBill = async (orderId: number, orderNumber: string) => {
    try {
      const response = await apiService.sendOrderDetails(orderId);
      if (response.isSuccess) {
        toast.success(`Bill sent successfully for order ${orderNumber}`);
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to send bill');
      }
    } catch (error) {
      console.error('Error sending bill:', error);
      toast.error('Error sending bill');
    }
  };

  // Exotel click-to-call state
  const [showExotelModal, setShowExotelModal]       = useState(false);
  const [selectedOrderForExotel, setSelectedOrder]  = useState<Order | null>(null);
  const [agentNumber, setAgentNumber]               = useState(() => localStorage.getItem('exotel_agent_number') || '');
  const [exotelCalling, setExotelCalling]           = useState(false);

  const handleOpenExotelModal = (order: Order) => {
    setSelectedOrder(order);
    setShowExotelModal(true);
  };

  const handleExotelCall = async () => {
    if (!selectedOrderForExotel || !agentNumber.trim()) return;
    localStorage.setItem('exotel_agent_number', agentNumber.trim());
    setExotelCalling(true);
    try {
      const result = await apiService.exotelCallOrder(selectedOrderForExotel.id, agentNumber.trim());
      if (result.isSuccess) {
        toast.success(`Calling ${selectedOrderForExotel.customerName} (${selectedOrderForExotel.customerMobile})...`);
        setShowExotelModal(false);
      } else {
        toast.error(result.message || 'Failed to initiate call');
      }
    } catch {
      toast.error('Failed to initiate call');
    } finally {
      setExotelCalling(false);
    }
  };

  const handleCallOrderConfirmation = async (orderId: number, orderNumber: string) => {
    try {
      toast.loading(`Calling customer for order ${orderNumber}...`, { id: 'order-call' });
      const response = await apiService.callOrderConfirmation(orderId);
      if (response?.message?.toLowerCase().includes('success') || response?.callSid) {
        toast.success(`Confirmation call initiated for ${orderNumber}`, { id: 'order-call' });
      } else {
        toast.error(response?.message || 'Failed to initiate call', { id: 'order-call' });
      }
    } catch (error) {
      console.error('Error initiating confirmation call:', error);
      toast.error('Error initiating confirmation call', { id: 'order-call' });
    }
  };

  const handleSendPdfBill = async (orderId: number, orderNumber: string) => {
    try {
      toast.loading(`Generating PDF for ${orderNumber}...`, { id: 'pdf-bill' });
      const response = await apiService.sendBill(orderId);
      if (response.isSuccess) {
        toast.success(`PDF bill sent successfully for ${orderNumber}`, { id: 'pdf-bill' });
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to send PDF bill', { id: 'pdf-bill' });
      }
    } catch (error) {
      console.error('Error sending PDF bill:', error);
      toast.error('Error sending PDF bill', { id: 'pdf-bill' });
    }
  };

  const handleSendPaymentQr = async (orderId: number, orderNumber: string) => {
    try {
      toast.loading(`Sending payment QR for ${orderNumber}...`, { id: 'payment-qr' });
      const response = await apiService.sendPaymentQr(orderId);
      if (response.isSuccess) {
        toast.success(`Payment QR sent to customer for ${orderNumber}`, { id: 'payment-qr' });
      } else {
        toast.error(response.errors?.join(', ') || 'Failed to send payment QR', { id: 'payment-qr' });
      }
    } catch (error) {
      console.error('Error sending payment QR:', error);
      toast.error('Error sending payment QR', { id: 'payment-qr' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
            <ShoppingCart className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Orders</h1>
            <p className="text-sm text-gray-500">Manage customer orders and tracking</p>
          </div>
        </div>
        
        <Link
          to="/orders/create"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Order
        </Link>
      </div>

      {/* Revenue Summary Bar */}
      {daySummary && (
        <div className="grid grid-cols-4 gap-3">
          <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
            <p className="text-xs font-medium text-blue-600 mb-0.5">Total Expected</p>
            <p className="text-xl font-bold text-blue-800">₹{daySummary.totalExpected.toLocaleString('en-IN')}</p>
            <p className="text-xs text-blue-500">{daySummary.totalOrders} orders</p>
            {daySummary.totalDiscount > 0 && (
              <p className="text-xs font-medium text-red-500 mt-1">
                −₹{daySummary.totalDiscount.toLocaleString('en-IN')} discount given
              </p>
            )}
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
            <p className="text-xs font-medium text-emerald-600 mb-0.5">Closed</p>
            <p className="text-xl font-bold text-emerald-800">₹{daySummary.closed.toLocaleString('en-IN')}</p>
            <p className="text-xs text-emerald-500">{daySummary.closedOrders} payment received</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <p className="text-xs font-medium text-amber-600 mb-0.5">Waiting</p>
            <p className="text-xl font-bold text-amber-800">₹{daySummary.waiting.toLocaleString('en-IN')}</p>
            <p className="text-xs text-amber-500">{daySummary.waitingOrders} in progress</p>
          </div>
          <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
            <p className="text-xs font-medium text-violet-600 mb-0.5">Total Service Cost</p>
            <p className="text-xl font-bold text-violet-800">₹{daySummary.totalServiceCost.toLocaleString('en-IN')}</p>
            <p className="text-xs text-violet-500">Technician earnings</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        {/* Quick Date Filters + Date Range */}
        <div className="flex flex-wrap items-end gap-2 mb-4">
          {([
            { label: 'Today', value: 'today' },
            { label: 'Tomorrow', value: 'tomorrow' },
            { label: 'Next 7 Days', value: 'next7days' },
          ] as const).map((f) => (
            <button
              key={f.value}
              onClick={() => applyDateFilter(f.value)}
              className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
                selectedDateFilter === f.value
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}

          <div className="flex items-end gap-2 ml-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => { setSelectedDateFilter('custom'); setStartDate(e.target.value); }}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => { setSelectedDateFilter('custom'); setEndDate(e.target.value); }}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search orders..."
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Technician Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Technician</label>
            <select
              value={technicianFilter}
              onChange={(e) => { setTechnicianFilter(e.target.value); setCurrentPage(1); }}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Technicians</option>
              {technicians.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Filter Actions */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-500">
            Showing {orders.length} of {totalCount} orders
          </div>
          <button
            onClick={resetFilters}
            className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            Reset Filters
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingCart className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No orders found</h3>
            <p className="text-gray-500">Create your first order to get started.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Device</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Technician</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parts</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{order.customerName}</div>
                            <div className="text-sm text-gray-500">{order.customerMobile}</div>
                          </div>
                          <button
                            onClick={() => handleOpenExotelModal(order)}
                            className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 hover:text-blue-700 transition-colors shrink-0 mt-0.5"
                            title="Call Customer"
                          >
                            <PhoneCall className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{order.makeName}</div>
                          <div className="text-sm text-gray-500">{order.modelName}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 max-w-[200px]">
                        <div className="text-sm text-gray-700 leading-snug line-clamp-2" title={order.customerAddress}>
                          {order.customerAddress || <span className="text-gray-400">—</span>}
                        </div>
                        {order.nearestStation && (
                          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-violet-50 text-violet-700 text-xs font-medium rounded-full">
                            <Train className="h-3 w-3" />
                            {order.nearestStation}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(order.orderStatus)}`}>
                          {order.orderStatus}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(order.finalAmount)}</div>
                          <div className="text-sm text-gray-500">{order.paymentMethod?.toLowerCase() === 'online' ? 'Online' : order.paymentMethod?.toLowerCase() === 'cod' ? 'COD' : order.paymentMethod}</div>
                          {order.totalServiceCost > 0 && (
                            <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">
                              Svc: {formatCurrency(order.totalServiceCost)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{order.technicianName || <span className="text-gray-400">—</span>}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {order.partsIssued
                          ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">Part Issued</span>
                          : <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Part Not Issued</span>
                        }
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(order.orderDate)}
                        {order.scheduledDate && order.scheduledTime &&
                          !['Payment Received', 'Service Done', 'Cancelled'].includes(order.orderStatus) &&
                          scheduleChip(order.scheduledDate, order.scheduledTime)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="relative">
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowActions(showActions === order.id ? null : order.id); }}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>

                          {showActions === order.id && (
                            <div onMouseDown={(e) => e.stopPropagation()} className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-10">
                              <div className="py-1">
                                <Link
                                  to={`/orders/edit/${order.id}`}
                                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  onClick={() => setShowActions(null)}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit Order
                                </Link>
                                <Link
                                  to={`/orders/edit/${order.id}`}
                                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                  onClick={() => setShowActions(null)}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </Link>
                                <button
                                  onClick={() => {
                                    setIssuePartsOrder(order);
                                    setShowActions(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-violet-600 hover:bg-violet-50"
                                >
                                  <Package className="h-4 w-4 mr-2" />
                                  Issue Parts
                                </button>
                                <button
                                  onClick={() => {
                                    handleSendBill(order.id, order.orderNumber);
                                    setShowActions(null);
                                  }}
                                  disabled={order.orderStatus !== 'Payment Received'}
                                  className={`flex items-center w-full px-4 py-2 text-sm ${
                                    order.orderStatus === 'Payment Received'
                                      ? 'text-green-600 hover:bg-green-50'
                                      : 'text-gray-400 cursor-not-allowed'
                                  }`}
                                >
                                  <Send className="h-4 w-4 mr-2" />
                                  Send Bill
                                </button>
                                <button
                                  onClick={() => {
                                    handleSendPdfBill(order.id, order.orderNumber);
                                    setShowActions(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50"
                                >
                                  <Send className="h-4 w-4 mr-2" />
                                  Send PDF Bill
                                </button>
                                <button
                                  onClick={() => {
                                    handleSendPaymentQr(order.id, order.orderNumber);
                                    setShowActions(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-purple-600 hover:bg-purple-50"
                                >
                                  <Send className="h-4 w-4 mr-2" />
                                  Send Payment QR
                                </button>
                                <button
                                  onClick={() => {
                                    handleOpenExotelModal(order);
                                    setShowActions(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50"
                                >
                                  <PhoneCall className="h-4 w-4 mr-2" />
                                  Call Customer
                                </button>
                                <button
                                  onClick={() => {
                                    handleCallOrderConfirmation(order.id, order.orderNumber);
                                    setShowActions(null);
                                  }}
                                  className="flex items-center w-full px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
                                >
                                  <Phone className="h-4 w-4 mr-2" />
                                  Call Order Confirmation
                                </button>
                                <button
                                  onClick={() => handleDeleteOrder(order.id, order.orderNumber)}
                                  className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Order
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>


          </>
        )}
      </div>

      {/* Pagination */}
      {!loading && orders.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
                className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = Math.max(1, currentPage - 2) + i;
                if (page > totalPages) return null;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 text-sm font-medium rounded-md ${
                      page === currentPage
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
                className="px-3 py-1 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {issuePartsOrder && (
        <IssuePartsModal
          order={issuePartsOrder}
          darkStores={darkStores}
          onClose={() => setIssuePartsOrder(null)}
          onSuccess={() => {}}
        />
      )}

      {/* Exotel Call Customer Modal */}
      {showExotelModal && selectedOrderForExotel && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center px-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowExotelModal(false)} />
            <div className="inline-block align-bottom bg-white rounded-xl px-6 pt-6 pb-5 text-left shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full relative">
              <button onClick={() => setShowExotelModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <PhoneCall className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Call Customer</h3>
                  <p className="text-sm text-gray-500">{selectedOrderForExotel.customerName} · {selectedOrderForExotel.customerMobile}</p>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Phone Number</label>
                <input
                  type="tel"
                  value={agentNumber}
                  onChange={e => setAgentNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter your 10-digit number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
                <p className="text-xs text-gray-400 mt-1">Exotel will call you first, then connect to the customer.</p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowExotelModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExotelCall}
                  disabled={exotelCalling || !agentNumber.trim()}
                  className="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exotelCalling
                    ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    : <PhoneCall className="h-4 w-4 mr-2" />}
                  {exotelCalling ? 'Calling...' : 'Call Now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage; 
